"use client";
import { Button } from "components/UI/Buttons/Button";
import React, { useState } from "react";
import s from "./SectionContactsForm.module.scss";
import { Container } from "components/layout/Container";
import { Heading } from "components/layout/Heading";
import { Input } from "components/form/Input";
import { Textarea } from "components/form/Textarea";
import cn from "classnames";
import { Formik } from "formik";
import { AnimatedNumber } from "@/lib/priceCalculator";
import cs from "@/styles/modules/card.module.scss";
import Image from "next/image";
import { contactFormSchema } from "@/components/pages/about-us/contacts/contactFormSchema";

type SectionContactsFormProps = {
  heading: string;
  subheading: string;
};

export type ContactFormPayload = {
  name: string;
  email: string;
  message: string;
};

export const SectionContactsForm = ({ heading, subheading }: SectionContactsFormProps) => {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  return (
    <section className={s.section}>
      <Container className={s.container}>
        <div className={s.left}>
          <Heading align="left" title={heading} subheading={subheading} />
        </div>
        <div className={s.right}>
          {success && (
            <div className={cn(cs.cardGlassHover, s.resultWrap)}>
              <Image
                src="/img/misc/email-success.png"
                width={110}
                height={110}
                quality={100}
                alt=""
                className={s.resultImg}
              />
              <h2 className={cn(cs.h3sm, s.resultTitle)}>
                Contact form <br />
                has been submitted successfully
              </h2>
              <p className={s.resultText}>We will contact you as soon as possible.</p>
              <Button href="/" outline>
                Back to Homepage
              </Button>
            </div>
          )}
          {error && (
            <div className={cn(cs.cardGlassHover, s.resultWrap)}>
              <Image
                src="/img/misc/email-failed.png"
                width={110}
                height={110}
                quality={100}
                alt=""
                className={s.resultImg}
              />
              <h2 className={cn(cs.h3sm, s.resultTitle)}>
                An error has occurred, <br />
                the contact form has not been sent
              </h2>
              <p className={s.resultText}>Sorry! Something went wrong.</p>
              <Button
                onClick={() => {
                  setError(false);
                }}
              >
                Try Again
              </Button>
            </div>
          )}
          <Formik
            initialValues={{
              name: "",
              email: "",
              message: "",
            }}
            validationSchema={contactFormSchema}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              try {
                const result = await fetch("/api/contacts", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify(values),
                });
                const { success } = await result.json();
                if (!success) throw new Error();
                resetForm();
                setSuccess(true);
              } catch (err) {
                setError(true);
              }

              setSubmitting(false);
            }}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => {
              return (
                <form
                  onSubmit={handleSubmit}
                  noValidate
                  className={cn(cs.cardGlassHover, s.form, {
                    [s.hidden]: success || error,
                  })}
                >
                  <div className={s.formInner}>
                    <label className={s.label}>
                      <span className={s.labelName}>Your name</span>
                      <Input
                        name="name"
                        placeholder="Input your name"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.name}
                        valid={!(touched.name && errors.name?.length)}
                        required
                      />
                      {!!(touched.name && errors.name) && <span className={s.error}>{errors.name}</span>}
                    </label>
                    <label className={s.label}>
                      <span className={s.labelName}>Your email</span>
                      <Input
                        name="email"
                        type="email"
                        placeholder="Input your email"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.email}
                        valid={!(touched.email && errors.email?.length)}
                        required
                      />
                      {!!(touched.email && errors.email) && <span className={s.error}>{errors.email}</span>}
                    </label>

                    <label className={cn(s.label, s.labelTextarea)}>
                      <span className={s.labelName}>Your message</span>
                      <Textarea
                        name="message"
                        placeholder="Input your message"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.message}
                        maxLength={1000}
                        valid={!(touched.message && errors.message?.length)}
                        required
                      />
                      <div className={s.messageCount}>
                        <AnimatedNumber number={values.message?.trim()?.length || 0} /> / 1000
                      </div>
                      {!!(touched.message && errors.message) && (
                        <span className={s.error}>{errors.message}</span>
                      )}
                    </label>
                  </div>

                  <div className={s.btnWrap}>
                    <Button
                      className={s.button}
                      htmlType={"submit"}
                      loading={isSubmitting}
                      disabled={isSubmitting}
                    >
                      Submit
                    </Button>
                  </div>
                </form>
              );
            }}
          </Formik>
        </div>
      </Container>
    </section>
  );
};
